import {
  useCallback,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { useIdTokenAndTenantId } from '@/hooks/useIdTokenAndTenantId'
import { useIsMobile } from '@/hooks/use-mobile'
import { queryKeys, universalSearch } from '@/data/queries'
import { DEFAULT_STALE_TIME } from '@/data/constants'
import type { SearchResourceType, SearchResultItem } from '@/data/entities'
import { TypeFilterBar } from './TypeFilterBar'
import { PreviewPanel } from './PreviewPanel'
import { FileText, File, FolderOpen, Users, Video } from 'lucide-react'

const RESOURCE_TYPE_LABELS: Record<string, string> = {
  SOP: 'SOPs',
  DOCUMENT: 'Documents',
  PROJECT: 'Projects',
  USER: 'Members',
  VIDEO_CLIP: 'Video Clips',
}

const RESOURCE_TYPE_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  SOP: FileText,
  DOCUMENT: File,
  PROJECT: FolderOpen,
  USER: Users,
  VIDEO_CLIP: Video,
}

// Order for display
const TYPE_ORDER = ['SOP', 'DOCUMENT', 'PROJECT', 'USER', 'VIDEO_CLIP']

export function UniversalSearch() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [typeFilters, setTypeFilters] = useState<SearchResourceType[]>([])
  const [selectedItem, setSelectedItem] = useState<SearchResultItem | null>(
    null,
  )
  const debouncedQuery = useDeferredValue(query)
  const { idToken, TENANT_ID } = useIdTokenAndTenantId()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const commandRef = useRef<HTMLDivElement>(null)

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setQuery('')
      setTypeFilters([])
      setSelectedItem(null)
    }
  }, [open])

  // Search query
  const shouldSearch =
    debouncedQuery.trim().length >= 2 && !!idToken && !!TENANT_ID
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.search.byQuery(
      TENANT_ID ?? '',
      debouncedQuery,
      typeFilters.length > 0 ? typeFilters : undefined,
    ),
    queryFn: () =>
      universalSearch({
        token: idToken!,
        tenantId: TENANT_ID!,
        query: debouncedQuery,
        types: typeFilters.length > 0 ? typeFilters : undefined,
      }),
    enabled: shouldSearch,
    staleTime: DEFAULT_STALE_TIME,
  })

  // Track selection for preview via cmdk's data-selected attribute
  useEffect(() => {
    if (!commandRef.current) return
    const observer = new MutationObserver(() => {
      const selected = commandRef.current?.querySelector(
        '[data-selected="true"]',
      )
      if (selected) {
        const itemData = selected.getAttribute('data-search-item')
        if (itemData) {
          try {
            setSelectedItem(JSON.parse(itemData))
          } catch {
            // ignore parse errors
          }
        }
      }
    })
    observer.observe(commandRef.current, {
      attributes: true,
      subtree: true,
      attributeFilter: ['data-selected'],
    })
    return () => observer.disconnect()
  }, [open])

  const handleSelect = useCallback(
    (item: SearchResultItem) => {
      if (!TENANT_ID) return
      setOpen(false)

      switch (item.resourceType) {
        case 'SOP':
          if (item.projectId) {
            navigate({
              to: '/org/$orgId/projects/$projectId/sops/$sopId',
              params: {
                orgId: TENANT_ID,
                projectId: item.projectId,
                sopId: item.id,
              },
            })
          }
          break
        case 'DOCUMENT':
          if (item.projectId) {
            navigate({
              to: '/org/$orgId/projects/$projectId',
              params: { orgId: TENANT_ID, projectId: item.projectId },
            })
          }
          break
        case 'PROJECT':
          navigate({
            to: '/org/$orgId/projects/$projectId',
            params: { orgId: TENANT_ID, projectId: item.id },
          })
          break
        case 'USER':
          navigate({
            to: '/org/$orgId/documents',
            params: { orgId: TENANT_ID },
          })
          break
        case 'VIDEO_CLIP': {
          const docId = item.metadata.documentId as string | undefined
          if (docId && item.projectId) {
            navigate({
              to: '/org/$orgId/projects/$projectId',
              params: { orgId: TENANT_ID, projectId: item.projectId },
            })
          }
          break
        }
      }
    },
    [TENANT_ID, navigate],
  )

  // Sort groups by defined order
  const sortedGroups = data
    ? TYPE_ORDER.filter((type) => data.results[type]?.length)
    : []

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogHeader className="sr-only">
        <DialogTitle>Search</DialogTitle>
        <DialogDescription>Search across all resources</DialogDescription>
      </DialogHeader>
      <DialogContent
        className={`overflow-hidden p-0 ${isMobile ? 'max-w-[95vw]' : 'max-w-3xl'}`}
      >
        <div className="flex">
          {/* Left side: Command list */}
          <div className="min-w-0 flex-1">
            <Command ref={commandRef} shouldFilter={false}>
              <CommandInput
                placeholder="Search everything..."
                value={query}
                onValueChange={setQuery}
              />
              <TypeFilterBar value={typeFilters} onChange={setTypeFilters} />
              <CommandList className="max-h-[400px]">
                {shouldSearch && isLoading && (
                  <div className="space-y-2 p-4">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                )}

                {shouldSearch && !isLoading && data?.total === 0 && (
                  <CommandEmpty>No results found.</CommandEmpty>
                )}

                {!shouldSearch && query.length === 0 && (
                  <div className="text-muted-foreground px-4 py-8 text-center text-sm">
                    Start typing to search across SOPs, documents, projects,
                    members, and video clips.
                  </div>
                )}

                {!shouldSearch && query.length > 0 && query.length < 2 && (
                  <div className="text-muted-foreground px-4 py-8 text-center text-sm">
                    Type at least 2 characters to search.
                  </div>
                )}

                {sortedGroups.map((type) => {
                  const items = data!.results[type]
                  const Icon = RESOURCE_TYPE_ICONS[type]
                  return (
                    <CommandGroup
                      key={type}
                      heading={
                        <span className="flex items-center gap-1.5">
                          {Icon && <Icon className="size-3" />}
                          {RESOURCE_TYPE_LABELS[type] ?? type}
                        </span>
                      }
                    >
                      {items.map((item) => (
                        <CommandItem
                          key={`${item.resourceType}-${item.id}`}
                          value={`${item.resourceType}-${item.id}`}
                          data-search-item={JSON.stringify(item)}
                          onSelect={() => handleSelect(item)}
                          className="gap-3"
                        >
                          <SearchResultRow item={item} />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )
                })}
              </CommandList>
            </Command>
          </div>

          {/* Right side: Preview panel (desktop only) */}
          {!isMobile && shouldSearch && data && data.total > 0 && (
            <div className="w-72 border-l">
              <PreviewPanel item={selectedItem} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function SearchResultRow({ item }: { item: SearchResultItem }) {
  const Icon = RESOURCE_TYPE_ICONS[item.resourceType]
  return (
    <>
      {Icon && <Icon className="text-muted-foreground size-4 shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm">{item.title}</div>
        {item.subtitle && (
          <div className="text-muted-foreground truncate text-xs">
            {item.subtitle}
          </div>
        )}
      </div>
      {item.projectName && (
        <span className="text-muted-foreground shrink-0 text-xs">
          {item.projectName}
        </span>
      )}
    </>
  )
}
